import React from 'react';
import { observer } from 'mobx-react';
import { Modal, Theme, Body } from '@routific/hive';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store/mst';
import Checkbox from '../shared/Checkbox';
import DeletePublishedVisitConfirmModal from './DeletePublishedVisitConfirmModal';

interface Props {
  visible: boolean;
  ordersCount: number;
  orderName: string;
  onOk: () => void;
  onCancel: () => void;
  allowOptOut?: boolean;
  hasPublishedOrders?: boolean;
}

const DeleteVisitConfirmModal: React.FC<Props> = ({
  visible, ordersCount, orderName, onOk, onCancel, allowOptOut = true, hasPublishedOrders = false,
}) => {
  const store = useStore();
  const { t } = useTranslation(['components', 'common']);

  const handleCheckboxChange = (checked: boolean) => {
    store.ui.setDisableConfirmDeleteVisitModal(checked);
  };

  if (hasPublishedOrders) {
    return (
      <DeletePublishedVisitConfirmModal
        visible={visible}
        ordersCount={ordersCount}
        onOk={onOk}
        onCancel={onCancel}
      />
    );
  }

  const getConfirmationText = () => {
    if (ordersCount > 1) {
      return t('components:deleteVisitsConfirmModal.bulk_text', { count: ordersCount });
    }
    if (orderName) {
      return t('components:deleteVisitsConfirmModal.text', { visitName: orderName });
    }
    return t('components:deleteVisitsConfirmModal.nameless_single_text', { count: 1 });
  };

  return (
    <Modal
      size="small"
      centered
      visible={visible}
      title={
        ordersCount > 1
          ? `${t('components:deleteVisitsConfirmModal.bulk_title', { count: ordersCount })}`
          : `${t('components:deleteVisitsConfirmModal.title')}`
      }
      onOk={onOk}
      onCancel={onCancel}
      destroyOnClose
      okButtonProps={{ danger: true }}
      okText={t('components:deleteVisitsConfirmModal.button.text')}
      data-cy="delete-visit-confirmation-modal"
    >
      <Body>
        {getConfirmationText()}
      </Body>
      {allowOptOut && <Checkbox
        onChange={handleCheckboxChange}
        label={t('common:disableShowAgain')}
        sx={{ marginTop: Theme.Spacing.mdSpacing }}
      />}
    </Modal>
  );
};

export default observer(DeleteVisitConfirmModal);
